import { useMemo } from "react";
import { ThemeProvider as EmotionThemeProvider } from "@emotion/react";
import {
  ThemeProvider as MuiThemeProvider,
  createTheme,
  useTheme as useMuiTheme,
} from "@mui/material/styles";
import styled from "@emotion/styled";
import { SidekickTheme, useTheme } from "@avenue-8/sidekick-ui";
import { findThemeById } from "./presentation-themes";

type WrapperProps = {
  $fontFaceRules: string;
  $bodyFont: string;
  $titleFont: string;
  $textPrimary: string;
  $titlePrimary: string;
  $background: string;
  $applyColors: boolean;
};

const PresentationScopeWrapper = styled.div<WrapperProps>`
  ${(p) => p.$fontFaceRules}
  ${(p) =>
    p.$applyColors && p.$background && `background-color: ${p.$background};`}

  &,
  & .MuiTypography-root {
    ${(p) => p.$bodyFont && `font-family: ${p.$bodyFont};`}
    ${(p) => p.$applyColors && `color: ${p.$textPrimary};`}
  }

  & h1,
  & h2,
  & h3,
  & h4,
  & h5,
  & h6,
  & .MuiTypography-h1,
  & .MuiTypography-h2,
  & .MuiTypography-h3,
  & .MuiTypography-h4,
  & .MuiTypography-h5,
  & .MuiTypography-h6 {
    ${(p) => p.$titleFont && `font-family: ${p.$titleFont};`}
    ${(p) => p.$applyColors && `color: ${p.$titlePrimary};`}
  }
`;

type Props = { themeId: string; children: React.ReactNode };

export const PresentationThemeProvider = ({ themeId, children }: Props) => {
  const baseMuiTheme = useMuiTheme();
  const sidekickTheme = useTheme();
  const override = findThemeById(themeId);

  const computed = useMemo(() => {
    if (!override) return null;

    const mergedSidekickTheme: SidekickTheme = {
      ...sidekickTheme,
      text: {
        ...sidekickTheme.text,
        fonts: {
          ...sidekickTheme.text.fonts,
          primary: override.theme.text.font.family,
        },
        colors: {
          ...sidekickTheme.text.colors,
          ...override.theme.text.colors,
        },
      },
      title: {
        ...sidekickTheme.title,
        fonts: {
          ...sidekickTheme.title.fonts,
          primary: override.theme.title.font.family,
        },
        colors: {
          ...sidekickTheme.title.colors,
          ...override.theme.title.colors,
        },
      },
      colors: { ...sidekickTheme.colors, ...override.theme.colors },
    };

    const textPrimary = override.theme.text.colors.primary;
    const textSecondary = override.theme.text.colors.secondary;
    const titlePrimary = override.theme.title.colors.primary;
    const bodyFont = override.theme.text.font.family;
    const titleFont = override.theme.title.font.family;
    const background = override.theme.colors?.background;

    const mergedMuiTheme = createTheme(baseMuiTheme, {
      palette: {
        mode: "light",
        text: { primary: textPrimary, secondary: textSecondary },
        primary: { main: textPrimary },
        ...(background && {
          background: { default: background, paper: background },
        }),
      },
      typography: {
        fontFamily: bodyFont,
        h1: { fontFamily: titleFont, color: titlePrimary },
        h2: { fontFamily: titleFont, color: titlePrimary },
        h3: { fontFamily: titleFont, color: titlePrimary },
        h4: { fontFamily: titleFont, color: titlePrimary },
        h5: { fontFamily: titleFont, color: titlePrimary },
        h6: { fontFamily: titleFont, color: titlePrimary },
        body1: { color: textPrimary },
        body2: { color: textSecondary },
        subtitle1: { color: textPrimary },
        subtitle2: { color: textPrimary },
        overline: { color: textPrimary },
      },
      sidekick: mergedSidekickTheme,
    });

    const fontFaceRules = [override.theme.text.font, override.theme.title.font]
      .flatMap(({ name, faces }) =>
        faces.map(
          ({ url, weight }) =>
            `@font-face { font-family: "${name}"; src: url("${url}") format("woff2"); font-weight: ${weight}; font-display: block; }`,
        ),
      )
      .join("\n");

    const wrapperProps: WrapperProps = {
      $fontFaceRules: fontFaceRules,
      $bodyFont: override.theme.text.font.family,
      $titleFont: override.theme.title.font.family,
      $textPrimary: override.theme.text.colors.primary,
      $titlePrimary: override.theme.title.colors.primary,
      $background: override.theme.colors?.background ?? "",
      $applyColors: true,
    };

    return { mergedSidekickTheme, mergedMuiTheme, wrapperProps };
  }, [override, sidekickTheme, baseMuiTheme]);

  const mergedMuiTheme = computed?.mergedMuiTheme ?? baseMuiTheme;
  const mergedSidekickTheme = computed?.mergedSidekickTheme ?? sidekickTheme;
  const wrapperProps: WrapperProps = computed?.wrapperProps ?? {
    $fontFaceRules: "",
    $bodyFont: sidekickTheme.text.fonts.primary,
    $titleFont: sidekickTheme.title.fonts.primary,
    $textPrimary: "",
    $titlePrimary: "",
    $background: "",
    $applyColors: false,
  };

  return (
    <MuiThemeProvider theme={mergedMuiTheme}>
      <EmotionThemeProvider theme={mergedSidekickTheme}>
        <PresentationScopeWrapper {...wrapperProps}>
          {children}
        </PresentationScopeWrapper>
      </EmotionThemeProvider>
    </MuiThemeProvider>
  );
};
